import { useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { logger } from '@/utils/logger/logger';

const TAG = 'StaleTabResync:';

/**
 * How long the tab must have been away before returning triggers a full resync.
 * Below this, per-query `staleTime` plus `refetchOnWindowFocus` already covers it.
 */
const RESYNC_AFTER_AWAY_MS = 5 * 60 * 1000;

/** How often to check the wall clock for a gap (sleep / heavy timer throttling). */
const HEARTBEAT_INTERVAL_MS = 60 * 1000;

/** Ignore a second resync trigger that lands right behind the first. */
const MIN_GAP_BETWEEN_RESYNCS_MS = 30 * 1000;

/**
 * Whether a query may be force-refreshed. `staleTime: Infinity` (planner) and
 * `refetchOnWindowFocus: false` (generate invoices/bills) both mean "this screen
 * owns its own refresh" — honouring either lets a screen declare it once.
 * Read off the observers because `query.options` is typed without these fields.
 */
const isRevalidationAllowed = (query: {
  observers: Array<{ options: { staleTime?: unknown; refetchOnWindowFocus?: unknown } }>;
}): boolean =>
  !query.observers.some(
    observer =>
      observer.options.staleTime === Infinity || observer.options.refetchOnWindowFocus === false
  );

/**
 * Resyncs all server state when the user returns to a long-idle tab.
 *
 * `refetchOnWindowFocus` covers the ordinary case but only reaches mounted,
 * stale-by-staleTime queries. After hours away everything on screen describes the
 * world as it was in the morning, so invalidate the whole cache once — including
 * disabled/unmounted queries, which are flagged and refetch on their next mount.
 *
 * Two triggers, because "away" has two shapes: the tab was hidden, or it stayed
 * visible while the machine slept (which never fires `visibilitychange`, hence the
 * wall-clock heartbeat).
 *
 * Mount once, at the router root.
 */
export function useStaleTabResync(): void {
  const queryClient = useQueryClient();

  const awaySinceRef = useRef<number | null>(null);
  const lastHeartbeatAtRef = useRef<number>(Date.now());
  const lastResyncAtRef = useRef<number>(Date.now());

  useEffect(() => {
    const resync = (reason: string, awayForMs: number) => {
      const now = Date.now();
      if (now - lastResyncAtRef.current < MIN_GAP_BETWEEN_RESYNCS_MS) return;
      lastResyncAtRef.current = now;

      logger.info(TAG, 'Resyncing server state after a long absence', { reason, awayForMs });

      queryClient.invalidateQueries({ predicate: isRevalidationAllowed });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        awaySinceRef.current = Date.now();
        return;
      }

      const awaySince = awaySinceRef.current;
      awaySinceRef.current = null;
      // Reset so a long hidden stretch doesn't immediately fire a second resync.
      lastHeartbeatAtRef.current = Date.now();

      if (awaySince === null) return;

      const awayForMs = Date.now() - awaySince;
      if (awayForMs >= RESYNC_AFTER_AWAY_MS) resync('visibility', awayForMs);
    };

    const handleHeartbeat = () => {
      const now = Date.now();
      const elapsedMs = now - lastHeartbeatAtRef.current;
      lastHeartbeatAtRef.current = now;

      // A clock jump far past the scheduled interval means the page was suspended.
      // Hidden tabs are the visibility handler's job.
      if (document.visibilityState !== 'visible') return;
      if (elapsedMs >= RESYNC_AFTER_AWAY_MS) resync('heartbeat', elapsedMs);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    const heartbeatId = window.setInterval(handleHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(heartbeatId);
    };
  }, [queryClient]);
}
